/**
 * Stale-while-revalidate cache for ProtectedRoute's MFA gate answer.
 *
 * ProtectedRoute wraps `/app/:feature`, `/log-job/:customerId`, and
 * `/jobs/:jobId` as separate <Route> elements (src/App.js), so navigating
 * between them unmounts and remounts ProtectedRoute. On every mount,
 * `mfaChecked` used to start `false` and the component rendered NOTHING
 * until its effect resolved — an effect that awaits
 * `supabase.auth.mfa.listFactors()` (a real network call in auth-js
 * 2.106.0) plus a second MFA read and two `company_settings`/
 * `tenant_mfa_policy` reads. That's a blank white-screen flash on every
 * Log Job / Job Detail entry and exit, for all five roles.
 *
 * This module holds the last SUCCESSFUL gate answer in memory so a remount
 * can render immediately with that answer while the full check still runs
 * in the background (every mount, unconditionally) and overwrites state
 * when it resolves — classic stale-while-revalidate. We never skip the
 * check; we only avoid blocking the first frame on it.
 *
 * SECURITY: this is a CLIENT UX GATE ONLY. The real authorization boundary
 * is server-side and does not read this cache at all —
 * `requireAal2` / `requireAal2IfEnrolled` in
 * supabase/functions/_shared/requireAal2.ts, and `current_user_satisfies_mfa()`
 * enforced inside RLS policies. A stale or wrong answer cached here can only
 * make the client UI render slightly out of date for a few hundred
 * milliseconds until the background check resolves — it can never grant
 * access the server would refuse, and it can never suppress the check
 * itself (the effect that performs the real check always runs on mount,
 * cache hit or not).
 *
 * A session-scoped cached answer is also consistent with the shipped
 * "enforce MFA at next login, grandfather active sessions" policy in
 * mfaEnrollmentBaseline.js — mid-session the gate's answer is not expected
 * to tighten, so briefly serving the last-known-good answer while a fresh
 * check is in flight introduces no new risk.
 *
 * Memory-only — mirrors the security model documented at the top of
 * src/utils/cache/dashboardCache.js: never localStorage/sessionStorage, so
 * it can't leak on a shared machine and disappears on tab close/reload.
 * Keyed by user id; a single entry (one signed-in user per tab), not a map.
 */

// { userId: string, mfaRequired: boolean, mfaEnrollmentRequired: boolean } | null
let entry = null;

// Bumped by every clear. A checkMfa already in flight when the session ends
// resolves AFTER the clear and would otherwise re-write an OPEN answer under
// the departing user's id — so the same account signing back in password-only
// (same tab, no reload) would seed `mfaRequired: false` and render the shell
// during revalidation. Callers capture the generation before their check and
// hand it back on write; a write whose generation is stale is dropped.
// Found independently by the security review AND the code review, 2026-09-04.
let generation = 0;

/** Capture before starting an async gate check; pass to writeMfaGate. */
export function mfaGateGeneration() {
  return generation;
}

/**
 * @param {string|null|undefined} userId
 * @returns {{ mfaRequired: boolean, mfaEnrollmentRequired: boolean } | null}
 */
export function readMfaGate(userId) {
  if (!userId || !entry || entry.userId !== userId) return null;
  return { mfaRequired: entry.mfaRequired, mfaEnrollmentRequired: entry.mfaEnrollmentRequired };
}

/**
 * @param {string|null|undefined} userId
 * @param {{ mfaRequired: boolean, mfaEnrollmentRequired: boolean }} answer
 */
export function writeMfaGate(userId, { mfaRequired, mfaEnrollmentRequired } = {}, gen) {
  if (!userId) return;
  // Superseded by a clear that landed while this check was in flight — drop
  // it rather than resurrect an answer for a session that has ended.
  if (gen !== undefined && gen !== generation) return;
  entry = { userId, mfaRequired: !!mfaRequired, mfaEnrollmentRequired: !!mfaEnrollmentRequired };
}

export function clearMfaGate() {
  entry = null;
  generation += 1;
}
